import { useState, useRef } from 'react'
import { Link } from 'react-router-dom'

export interface EntityDirectoryVendor {
  id: string
  user_id?: string
  vendor_name: string
  vendor_description?: string | null
  contact_name?: string | null
  vendor_email: string
  vendor_phone?: string | null
  website_url?: string | null
  vendor_logo_url?: string | null
  vendor_type?: 'broadcaster' | 'ad-creator'
}

interface EntityDirectoryCardProps {
  entity: EntityDirectoryVendor
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? 'h-5 w-5'}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className ?? 'h-5 w-5'}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

export default function EntityDirectoryCard({ entity }: EntityDirectoryCardProps) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')
  const copyFeedbackRef = useRef<HTMLDivElement>(null)

  const handleCopyEmail = async () => {
    if (!entity.vendor_email) return
    try {
      await navigator.clipboard.writeText(entity.vendor_email)
      setCopyStatus('copied')
      if (copyFeedbackRef.current) {
        copyFeedbackRef.current.textContent = 'Contact email copied to clipboard.'
      }
      setTimeout(() => {
        setCopyStatus('idle')
        if (copyFeedbackRef.current) {
          copyFeedbackRef.current.textContent = ''
        }
      }, 2500)
    } catch {
      setCopyStatus('error')
      if (copyFeedbackRef.current) {
        copyFeedbackRef.current.textContent = 'Copy failed. Please copy the email manually.'
      }
      setTimeout(() => {
        setCopyStatus('idle')
        if (copyFeedbackRef.current) {
          copyFeedbackRef.current.textContent = ''
        }
      }, 3000)
    }
  }

  const profileLink =
    entity.vendor_type === 'broadcaster'
      ? `/broadcasters/${entity.user_id || entity.id}`
      : entity.vendor_type === 'ad-creator'
        ? `/ad-creators/${entity.user_id || entity.id}`
        : null

  const cardContent = (
    <div
      className="rounded-lg border-2 flex flex-row overflow-hidden h-full min-h-[100px]"
      style={{
        backgroundColor: 'var(--bg-primary)',
        borderColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)',
      }}
    >
      {/* Column 1: Image */}
      <div
        className="w-28 sm:w-32 shrink-0 flex items-center justify-center"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          minHeight: '100px',
        }}
      >
        {entity.vendor_logo_url ? (
          <img
            src={entity.vendor_logo_url}
            alt={`${entity.vendor_name} logo`}
            className="w-full h-full object-contain object-center"
          />
        ) : (
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }} aria-hidden="true">
            No image
          </span>
        )}
      </div>

      {/* Column 2: Text */}
      <div className="flex flex-col flex-1 min-w-0 p-4 gap-0.5 justify-center">
        <h3 className="text-base font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
          {entity.vendor_name}
        </h3>

        {entity.contact_name && (
          <p className="text-xs leading-snug" style={{ color: 'var(--text-secondary)' }}>
            {entity.contact_name}
          </p>
        )}

        {entity.vendor_description && (
          <p className="text-xs line-clamp-2 leading-snug" style={{ color: 'var(--text-primary)' }}>
            {entity.vendor_description}
          </p>
        )}

        {/* Contact email with copy button */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <a
            href={`mailto:${entity.vendor_email}`}
            className="text-xs break-all underline min-w-0"
            style={{ color: 'var(--text-primary)' }}
          >
            {entity.vendor_email}
          </a>
          <button
            type="button"
            onClick={handleCopyEmail}
            className="p-1 rounded border shrink-0"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
            }}
            aria-label="Copy contact email"
            title="Copy contact email"
          >
            {copyStatus === 'copied' ? (
              <CheckIcon className="h-3.5 w-3.5" style={{ color: 'var(--color-success, green)' }} />
            ) : (
              <CopyIcon className="h-3.5 w-3.5" />
            )}
          </button>
        </div>

        <div
          ref={copyFeedbackRef}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        />
        {copyStatus === 'copied' && (
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Copied
          </span>
        )}
        {copyStatus === 'error' && (
          <span className="text-xs" style={{ color: 'var(--color-error)' }}>
            Copy failed.
          </span>
        )}

        {(entity.vendor_phone || entity.website_url) && (
          <p className="text-xs leading-snug" style={{ color: 'var(--text-secondary)' }}>
            {entity.vendor_phone && <span>Phone: {entity.vendor_phone}</span>}
            {entity.vendor_phone && entity.website_url && ' · '}
            {entity.website_url && (
              <a
                href={entity.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: 'var(--bg-interactive)' }}
              >
                Website
              </a>
            )}
          </p>
        )}

        {profileLink && (
          <Link
            to={profileLink}
            className="mt-1 text-xs font-semibold underline self-start"
            style={{ color: 'var(--bg-interactive)' }}
          >
            View profile
          </Link>
        )}
      </div>
    </div>
  )

  return <div className="h-full">{cardContent}</div>
}
